import {XIcon} from '@primer/octicons-react'
import {Heading, IconButton} from '@primer/react'
import {memo, useCallback} from 'react'
import styles from './DockedSidePanel.module.css'
import {useDockedPanel, useDockedPanelFocusRefs} from '../../contexts/DockedPanelContext'
import type {DockedPanelType} from '../../contexts/DockedPanelContext'

/** Portal region IDs for the docked side panel sections */
export const DOCKED_SIDE_PANEL_HEADER_ID = 'docked-side-panel-header'
export const DOCKED_SIDE_PANEL_BODY_ID = 'docked-side-panel-body'
export const DOCKED_SIDE_PANEL_FOOTER_ID = 'docked-side-panel-footer'

const panelHeadings: Record<Exclude<DockedPanelType, null>, string> = {
  comments: 'Comments',
  alerts: 'Alerts',
  overview: 'Overview',
}

interface DockedSidePanelContentProps {
  onClose: () => void
}

/** Content of the docked side panel, including header with close button and dynamic heading based on open panel */
export const DockedSidePanelContent = memo(function DockedSidePanelContent({onClose}: DockedSidePanelContentProps) {
  const {closeButtonRef, returnFocusRef} = useDockedPanelFocusRefs()
  const panelName = useDockedPanel()
  const heading = panelName ? panelHeadings[panelName] : 'Comments'

  const handleClose = useCallback(() => {
    const returnTarget = returnFocusRef.current
    onClose()
    // Return focus to the element that opened the panel
    if (returnTarget) {
      requestAnimationFrame(() => returnTarget.focus())
    }
  }, [onClose, returnFocusRef])

  return (
    <div className="d-flex flex-column height-full">
      {/* Header */}
      <div className={styles.Header}>
        <div className={styles.HeaderContent}>
          <Heading as="h3" className={styles.Heading}>
            {heading}
          </Heading>
          <IconButton
            ref={closeButtonRef}
            aria-label="Close side panel"
            icon={XIcon}
            variant="invisible"
            size="medium"
            onClick={handleClose}
          />
        </div>
        <div id={DOCKED_SIDE_PANEL_HEADER_ID} />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto pr-p-3" id={DOCKED_SIDE_PANEL_BODY_ID} />

      {/* Footer */}
      <div className="pr-p-3" id={DOCKED_SIDE_PANEL_FOOTER_ID} />
    </div>
  )
})
